import { useEffect, useState } from 'react';

/**
 * A CSS media query, readable from JS.
 *
 * For the cases a Tailwind breakpoint cannot reach: a value that is not a class
 * but a NUMBER the component computes with. The check-in map is the reason it
 * exists — its height feeds Leaflet's `fitBounds` padding and zoom solve, so
 * "shorter on a phone" cannot be `sm:h-40`, it has to be a different number.
 *
 * SSR- and jsdom-safe: `matchMedia` is absent in both, and the initial state
 * answers `false` rather than throwing. `addEventListener('change')` is the
 * modern API; the deprecated `addListener` is not shimmed because every browser
 * this product supports has had the former since 2016.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent): void => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
